import React from 'react';
import { GraduationCap, Award, Users, Heart, BookOpen, Globe, Microscope } from 'lucide-react';
import surgeonImage from '../images/surgen.png';
import vasha from '../images/vasha.png';

const About = () => {
  return (
    <div data-testid="about-page">
      {/* Hero Section with Background */}
      <section className="section-large bg-medical-light parallax-section" data-testid="about-hero">
        <div className="container">
          <div className="grid grid-2 gap-16 items-center">
            <div>
              <h1 className="heading-primary" data-testid="about-title">
                About Vasha Kaur
              </h1>
              <p className="text-large mb-8" data-testid="about-intro">
                A distinguished surgeon committed to providing exceptional patient care through 
                innovative surgical techniques, academic excellence, and compassionate medical practice.
              </p>
              <div className="grid grid-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Award className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">PhD in Metabolic Health</p>
                    <p className="text-gray-600 text-sm">Academic Excellence</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">2000+ Procedures</p>
                    <p className="text-gray-600 text-sm">Proven Experience</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="image-overlay">
                <img
  src={vasha}
  alt="Professor Vasha Kaur"
  className="w-96 h-96 object-cover rounded-2xl shadow-2xl"
  data-testid="about-image"
/>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications Section with Enhanced Design */}
      <section className="section" data-testid="qualifications-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-secondary" data-testid="qualifications-title">
              Qualifications & Education
            </h2>
            <p className="text-large" data-testid="qualifications-subtitle">
              A foundation of excellence built through rigorous training and continuous learning
            </p>
          </div>
          
          <div className="grid grid-2 gap-8">
            <div className="card" data-testid="medical-degree">
              <div className="card-header">
                <div className="service-icon mb-4">
                  <GraduationCap className="text-blue-600" size={32} />
                </div>
                <h3 className="heading-tertiary">Medical Education</h3>
              </div>
              <div className="card-content">
                <p className="mb-4">MBChB from the University of Edinburgh, followed by run-through specialised surgical training and advanced fellowships.</p>
                <ul className="space-y-2 text-gray-600">
                  <li>• Medical degree with honours</li>
                  <li>• Surgical residency training in London</li>
                  <li>• Advanced fellowship programmes</li>
                </ul>
              </div>
            </div>
            
            <div className="card" data-testid="surgical-training">
              <div className="card-header">
                <div className="service-icon mb-4">
                  <Microscope className="text-blue-600" size={32} />
                </div>
                <h3 className="heading-tertiary">Surgical Specialisation</h3>
              </div>
              <div className="card-content">
                <p className="mb-4">Advanced training in General Surgery with subspecialisation in Upper Gastrointestinal Surgery and Metabolic Surgery techniques.</p>
                <ul className="space-y-2 text-gray-600">
                  <li>• General Surgery certification</li>
                  <li>• Upper GI Surgery fellowship</li>
                  <li>• Metabolic Surgery specialisation</li>
                </ul>
              </div>
            </div>
            
            <div className="card" data-testid="professional-memberships">
              <div className="card-header">
                <div className="service-icon mb-4">
                  <Users className="text-blue-600" size={32} />
                </div>
                <h3 className="heading-tertiary">Professional Memberships</h3>
              </div>
              <div className="card-content">
                <p className="mb-4">Active membership in leading surgical societies and professional organisations worldwide.</p>
                <ul className="space-y-2 text-gray-600">
                  <li>• Royal College of Surgeons</li>
                  <li>• British Association of Metabolic &amp; Bariatric Surgery</li>
                  <li>• Association of Upper Gastrointestinal Surgeons</li>
                  <li>• International Federation of Surgery</li>
                </ul>
              </div>
            </div>
            
            <div className="card" data-testid="academic-position">
              <div className="card-header">
                <div className="service-icon mb-4">
                  <BookOpen className="text-blue-600" size={32} />
                </div>
                <h3 className="heading-tertiary">Academic Leadership</h3>
              </div>
              <div className="card-content">
                <p className="mb-4">Actively involved in medical education and research, supporting the development of future surgeons.</p>
                <ul className="space-y-2 text-gray-600">
                  <li>• Active research portfolio</li>
                  <li>• Postgraduate and Undergraduate Surgical Tutor</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Care Philosophy with Visual Elements */}
      <section className="section bg-gradient-modern" data-testid="philosophy-section">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="heading-secondary" data-testid="philosophy-title">Care Philosophy</h2>
              <p className="text-large" data-testid="philosophy-subtitle">
                Patient-centred care with a focus on compassionate treatment and excellent outcomes
              </p>
            </div>
            
            <div className="grid grid-2 gap-12 items-center">
              <div className="card" data-testid="philosophy-content">
                <div className="text-center mb-8">
                  <div className="service-icon mx-auto mb-4">
                    <Heart className="text-blue-600" size={40} />
                  </div>
                </div>
                <div className="space-y-6 text-lg leading-relaxed">
                  <p>
                    My approach to patient care is built on the foundation of treating each individual 
                    with dignity, respect, and personalised attention. I believe that effective 
                    surgical care extends beyond technical expertise to include clear communication, 
                    emotional support, and collaborative decision-making.
                  </p>
                  <p>
                    Every patient deserves to understand their condition, treatment options, and 
                    expected outcomes. I take the time to ensure that patients and their families 
                    are fully informed and comfortable with their treatment plan.
                  </p>
                  <p>
                    My commitment extends to staying at the forefront of surgical innovation, 
                    continuously updating skills and knowledge to offer the most advanced, 
                    minimally invasive techniques that promote faster recovery.
                  </p>
                </div>
              </div>
              
              <div className="image-overlay">
                <img 
  src={surgeonImage}     
               alt="Female healthcare professional during consultation"
                  className="w-full h-96 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements & Leadership Enhanced */}
      <section className="section" data-testid="achievements-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-secondary" data-testid="achievements-title">
              Major Achievements & Leadership Roles
            </h2>
            <p className="text-large" data-testid="achievements-subtitle">
              Recognition for excellence in surgery, research, and medical education
            </p>
          </div>
          
          <div className="grid grid-2 gap-12">
            <div className="space-y-8" data-testid="achievements-list">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="service-icon">
                    <Award className="text-blue-600" size={28} />
                  </div>
                  <h3 className="heading-tertiary">Clinical Achievements</h3>
                </div>
                <div className="space-y-6">
                  <div className="card">
                    <h4 className="font-semibold text-gray-900 mb-2">Pioneering Surgical Techniques</h4>
                    <p className="text-gray-600">Developed and implemented innovative approaches to complex gallbladder and hernia surgeries, leading to improved patient outcomes.</p>
                  </div>
                  <div className="card">
                    <h4 className="font-semibold text-gray-900 mb-2">Metabolic Surgery Excellence</h4>
                    <p className="text-gray-600">Recognised expertise in weight loss surgery with exceptional patient outcomes and industry-leading safety records.</p>
                  </div>
                  <div className="card">
                    <h4 className="font-semibold text-gray-900 mb-2">Research Publications</h4>
                    <p className="text-gray-600">Authored 50+ peer-reviewed publications in prestigious medical journals, contributing to surgical knowledge worldwide.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-8" data-testid="leadership-list">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="service-icon">
                    <Globe className="text-blue-600" size={28} />
                  </div>
                  <h3 className="heading-tertiary">Leadership Impact</h3>
                </div>
                <div className="space-y-6">
                  <div className="card">
                    <h4 className="font-semibold text-gray-900 mb-2">Department Leadership</h4>
                    <p className="text-gray-600">Led surgical departments and committees, driving improvements in patient care standards and surgical outcome protocols.</p>
                  </div>
                  <div className="card">
                    <h4 className="font-semibold text-gray-900 mb-2">Medical Education</h4>
                    <p className="text-gray-600">Mentored and trained hundreds of medical students, residents, and fellow surgeons, shaping the next generation of healthcare professionals.</p>
                  </div>
                  <div className="card">
                    <h4 className="font-semibold text-gray-900 mb-2">International Recognition</h4>
                    <p className="text-gray-600">Invited speaker at international conferences and active leadership roles in global surgical societies and medical organisations.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section bg-medical-light parallax-section" data-testid="about-cta">
        <div className="container text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-secondary mb-6">Experience Excellence in Surgical Care</h2>
            <p className="text-large mb-8">
              Benefit from Vasha Kaur&apos;s expertise and commitment to providing the highest 
              standard of surgical care with compassionate, patient-centred treatment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="btn btn-primary btn-large">
                Schedule Your Consultation
              </a>
              <a href="/services" className="btn btn-secondary btn-large">
                Explore Our Services
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
